import apiService from './apiService';
import { getApiBaseUrl } from '../config/apiConfig';

const PREFIX = '/kkp/distrib';
const IMPORT_TIMEOUT_MS = 300000;
const GEOCODE_TIMEOUT_MS = 180000;

async function request(promise) {
  const res = await promise;
  if (!res.success) {
    throw new Error(res.message || 'Erreur API');
  }
  return res;
}

async function downloadAuthenticated(path, fallbackName) {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('jwt_token');
  const url = path instanceof URL ? path : new URL(`${getApiBaseUrl()}${path}`);

  const response = await fetch(url.toString(), {
    headers: token ? { Authorization: `Bearer ${token}`, Accept: '*/*' } : { Accept: '*/*' },
  });
  const contentType = response.headers.get('Content-Type') || '';
  if (!response.ok) {
    if (contentType.includes('application/json')) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Erreur export (HTTP ${response.status})`);
    }
    throw new Error(`Erreur export (HTTP ${response.status})`);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/);
  const filename = match?.[1] || fallbackName;
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

const kkpDistribService = {
  async getMeta() {
    const res = await request(apiService.get(`${PREFIX}/meta`));
    return res.data;
  },

  async listZones(params = {}) {
    const res = await request(apiService.get(`${PREFIX}/zones`, params));
    return res.data?.zones ?? [];
  },

  async createZone(payload) {
    const res = await request(apiService.post(`${PREFIX}/zones`, payload));
    return res.data?.zone;
  },

  async updateZone(id, payload) {
    const res = await request(apiService.patch(`${PREFIX}/zones/${id}`, payload));
    return res.data?.zone;
  },

  async deleteZone(id) {
    const res = await request(apiService.delete(`${PREFIX}/zones/${id}`));
    return res.data;
  },

  async listPoints(params = {}) {
    const res = await request(apiService.get(`${PREFIX}/points`, params));
    return {
      points: res.data?.points ?? [],
      villes: res.data?.villes ?? [],
      pagination: res.pagination ?? { page: 1, pages: 0, total: 0 },
    };
  },

  async getTotaux(archived = false) {
    const res = await request(apiService.get(`${PREFIX}/totaux`, archived ? { archived: 1 } : {}));
    return res.data;
  },

  async getAgenceMag() {
    const res = await request(apiService.get(`${PREFIX}/agence-mag`));
    return res.data?.agence_mag ?? { paquets: 0, qte_mag: 0, paquets_set: 0, qte_set: 0 };
  },

  async saveAgenceMag(payload) {
    const body = typeof payload === 'number' ? { paquets: payload } : payload;
    const res = await request(apiService.put(`${PREFIX}/agence-mag`, body));
    return res.data?.agence_mag;
  },

  async createPoint(payload) {
    const res = await request(apiService.post(`${PREFIX}/points`, payload));
    return res.data?.point;
  },

  async updatePoint(id, payload) {
    const res = await request(apiService.patch(`${PREFIX}/points/${id}`, payload));
    return res.data?.point;
  },

  async deletePoint(id) {
    const res = await request(apiService.delete(`${PREFIX}/points/${id}`));
    return res.data;
  },

  async reorderPoints(zone, orderedIds) {
    const res = await request(apiService.post(`${PREFIX}/points/reorder`, {
      zone,
      ordered_ids: orderedIds,
    }));
    return res.data?.points ?? [];
  },

  async searchAddress(q, postcode = '') {
    const res = await request(apiService.get(`${PREFIX}/geocode-search`, {
      q,
      ...(postcode ? { postcode } : {}),
    }));
    return res.data;
  },

  async geocode(payload = {}) {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('jwt_token');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), GEOCODE_TIMEOUT_MS);
    try {
      const response = await fetch(`${getApiBaseUrl()}${PREFIX}/geocode`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.message || `Erreur géocodage (HTTP ${response.status})`);
      }
      return data.data;
    } finally {
      clearTimeout(timeoutId);
    }
  },

  async importExcel(file, { replace = true } = {}) {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('jwt_token');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('replace', replace ? '1' : '0');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), IMPORT_TIMEOUT_MS);

    try {
      const response = await fetch(`${getApiBaseUrl()}${PREFIX}/import`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
        signal: controller.signal,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.message || `Erreur import (HTTP ${response.status})`);
      }
      return data.data;
    } catch (err) {
      if (err?.name === 'AbortError') {
        throw new Error('Import trop long (délai dépassé). Réessayez ou découpez le fichier.');
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }
  },

  async exportExcel(zone = '') {
    const url = new URL(`${getApiBaseUrl()}${PREFIX}/export`);
    if (zone) url.searchParams.set('zone', zone);
    await downloadAuthenticated(url, 'kkp-distrib.xlsx');
  },

  async exportPdf(zone, support = 'set', ordreId = null) {
    const url = new URL(`${getApiBaseUrl()}${PREFIX}/export-pdf`);
    url.searchParams.set('zone', zone);
    if (support) url.searchParams.set('support', support);
    if (ordreId) url.searchParams.set('ordre_id', String(ordreId));
    await downloadAuthenticated(url, 'kkp-distrib.pdf');
  },

  async listOrdres(zone = '') {
    const res = await request(apiService.get(`${PREFIX}/ordres`, zone ? { zone } : {}));
    return res.data?.ordres ?? [];
  },

  async getOrdre(id) {
    const res = await request(apiService.get(`${PREFIX}/ordres/${id}`));
    return res.data?.ordre;
  },

  async createOrdre(payload) {
    const res = await request(apiService.post(`${PREFIX}/ordres`, payload));
    return res.data?.ordre;
  },

  async updateOrdre(id, payload) {
    const res = await request(apiService.patch(`${PREFIX}/ordres/${id}`, payload));
    return res.data?.ordre;
  },

  async deleteOrdre(id) {
    const res = await request(apiService.delete(`${PREFIX}/ordres/${id}`));
    return res.data;
  },

  async reorderOrdre(id, orderedIds) {
    const res = await request(apiService.post(`${PREFIX}/ordres/${id}/reorder`, {
      ordered_ids: orderedIds,
    }));
    return res.data?.points ?? [];
  },

  async refreshOrdre(id, includeZero = false) {
    const res = await request(apiService.post(`${PREFIX}/ordres/${id}/refresh`, {
      include_zero: includeZero,
    }));
    return res.data?.ordre;
  },

  async listCommentaires(params = {}) {
    const res = await request(apiService.get(`${PREFIX}/commentaires`, params));
    return res.data;
  },

  async createCommentaire(payload) {
    const res = await request(apiService.post(`${PREFIX}/commentaires`, payload));
    return res.data?.commentaire;
  },

  async updateCommentaire(id, payload) {
    const res = await request(apiService.patch(`${PREFIX}/commentaires/${id}`, payload));
    return res.data?.commentaire;
  },

  async deleteCommentaire(id) {
    const res = await request(apiService.delete(`${PREFIX}/commentaires/${id}`));
    return res.data;
  },

  async listLivraisons(months = 12) {
    const res = await request(apiService.get(`${PREFIX}/livraisons`, { months }));
    return res.data?.livraisons ?? [];
  },

  async upsertLivraison(payload) {
    const res = await request(apiService.put(`${PREFIX}/livraisons`, payload));
    return res.data?.livraison;
  },
};

export default kkpDistribService;
